import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ModernDatePicker from 'src/components/SharedComponents/ModernDatePicker/ModernDatePicker';
import ModernSelect, { ModernSelectOption } from 'src/components/ModernSelect/ModernSelect';
import Skeleton from 'src/components/Skeleton/Skeleton';
import Button from '../../../../components/Button';
import { FormHeader } from '../FormHeader';
import { fetchServiceRequestStats } from 'src/store/actions/serviceRequestStats';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';
import useFetchServiceRequestStatsFormData from 'src/pages/Reporting/CustomHooks/useFetchServiceRequestStatsFormData';
import './serviceRequestStatsForm.scss';

interface ServiceRequestStatsProps {
  properties: IProperty[];
}

const findSelectedOption = (
  options: ModernSelectOption[],
  value: string | undefined
): ModernSelectOption | null => {
  return (
    options.find((option: ModernSelectOption) => option.value === value) ?? null
  );
};

export const ServiceRequestStatsForm = ({
  properties,
}: ServiceRequestStatsProps) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);

  const {
    assetTypeOptions,
    serviceRequestTagOptions,
    issueTypeOptions,
    serviceProviderOptions,
    loading,
    error,
    fetchData,
  } = useFetchServiceRequestStatsFormData();

  const [formData, setFormData] = useState({
    fromDate: undefined,
    toDate: new Date(),
    tags: undefined,
    issueType: undefined,
    serviceProvider: undefined,
    assetType: undefined,
    propertyId: undefined,
  });

  const propertyOptions: ModernSelectOption[] =
    properties?.map((property: IProperty) => ({
      value: property.id,
      label: property.name,
    })) ?? [];

  const handleInputChange = (val: any, key: string) => {
    setFormData((prev: any) => ({ ...prev, [key]: val }));
  };

  const generateData = () => {
    dispatch(
      fetchServiceRequestStats({
        from: formData.fromDate
          ? moment(formData.fromDate).format('YYYY-MM-DD')
          : undefined,
        to: moment(formData.toDate).format('YYYY-MM-DD'),
        tags: formData.tags,
        issueType: formData.issueType,
        serviceProvider: formData.serviceProvider,
        assetType: formData.assetType,
        propertyId: formData.propertyId,
      })
    );
  };

  useEffect(() => {
    const loadFormData = async () => {
      const loginUserId = auth.user.id;
      const role = auth.user.roles[0].role.name;
      await fetchData(loginUserId, role);
    };

    loadFormData();
  }, []);

  if (loading) {
    return (
      <div className="service-request-stats-form-skeleton">
        <Skeleton variant="text" width="12rem" height="1.25rem" />
        <Skeleton variant="rect" width="100%" height="2.5rem" />
        <Skeleton variant="rect" width="100%" height="2.5rem" />
        <Skeleton variant="text" width="8rem" height="1.25rem" />
        {[...Array(5)].map((_: unknown, index: number) => (
          <Skeleton key={index} variant="rect" width="100%" height="2.5rem" />
        ))}
        <Skeleton variant="rect" width="10rem" height="2.25rem" />
      </div>
    );
  }

  if (error) {
    return <div>Error loading form data: {error}</div>;
  }

  return (
    <div>
      <h4>Select Report Dates</h4>
      <ModernDatePicker
        label="From Date"
        placeholder="Select Date"
        value={formData.fromDate ?? null}
        onChange={(date: Date | null) => handleInputChange(date, 'fromDate')}
      />
      <ModernDatePicker
        label="To Date"
        placeholder="Select Date"
        value={formData.toDate ?? null}
        onChange={(date: Date | null) => handleInputChange(date, 'toDate')}
      />
      <h4>Filter By:</h4>
      <FormHeader>Property</FormHeader>
      <ModernSelect
        size="regular"
        placeholder="Select Property"
        options={propertyOptions}
        value={findSelectedOption(propertyOptions, formData.propertyId)}
        onChange={(option: ModernSelectOption | null) =>
          handleInputChange(option?.value, 'propertyId')
        }
      />
      <FormHeader>Issue Type</FormHeader>
      <ModernSelect
        size="regular"
        placeholder="Select Issue Type"
        options={issueTypeOptions}
        value={findSelectedOption(issueTypeOptions, formData.issueType)}
        onChange={(option: ModernSelectOption | null) =>
          handleInputChange(option?.value, 'issueType')
        }
      />
      <FormHeader>Asset Type</FormHeader>
      <ModernSelect
        size="regular"
        placeholder="Select Asset Type"
        options={assetTypeOptions}
        value={findSelectedOption(assetTypeOptions, formData.assetType)}
        onChange={(option: ModernSelectOption | null) =>
          handleInputChange(option?.value, 'assetType')
        }
      />
      <FormHeader>Service Provider</FormHeader>
      <ModernSelect
        size="regular"
        placeholder="Select Service Provider"
        options={serviceProviderOptions}
        value={findSelectedOption(serviceProviderOptions, formData.serviceProvider)}
        onChange={(option: ModernSelectOption | null) =>
          handleInputChange(option?.value, 'serviceProvider')
        }
      />
      <FormHeader>Tag</FormHeader>
      <ModernSelect
        size="regular"
        placeholder="Select Tag"
        options={serviceRequestTagOptions}
        value={findSelectedOption(serviceRequestTagOptions, formData.tags)}
        onChange={(option: ModernSelectOption | null) =>
          handleInputChange(option?.value, 'tags')
        }
      />
      <div className="service-request-stats-form-actions">
        <Button
          color="teal"
          size="small"
          label="Generate Report"
          onClick={generateData}
        />
      </div>
    </div>
  );
};
